import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FiCalendar, FiUser, FiArrowLeft, FiClock, FiArrowRight } from 'react-icons/fi';
import api from '../utils/api';
import './BlogPost.css';

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/blog/${slug}`);
      const postData = response.data?.data || response.data;
      setPost(postData);
      setError(null);
    } catch (err) {
      console.error('Error fetching post:', err);
      setError('Blog post not found');
      setPost(null);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatReadingTime = (content) => {
    if (!content) return '1 min read';
    const text = content.replace(/<[^>]*>/g, ''); // Remove HTML tags
    const words = text.split(/\s+/).length;
    const minutes = Math.ceil(words / 200); // Average reading speed: 200 words per minute
    return `${minutes} min read`;
  };

  if (isLoading) {
    return (
      <div className="blog-post page-transition">
        <div className="container">
          <div className="blog-post-loading">Loading post...</div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-post page-transition">
        <div className="container">
          <div className="blog-post-error">
            <h2>Post Not Found</h2>
            <p>The blog post you're looking for doesn't exist or has been removed.</p>
            <Link to="/blog" className="btn-back">
              <FiArrowLeft /> Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-post page-transition">
      <Helmet>
        <title>{post ? `${post.title} - Couriex` : 'Blog Post - Couriex'}</title>
        <meta name="description" content={post?.excerpt || 'Read our latest blog post'} />
      </Helmet>
      {/* Hero Section */}
      <section className="blog-post-hero">
        <div className="container">
          <motion.div
            className="blog-post-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/blog" className="blog-post-back-link">
              <FiArrowLeft /> Back to Blog
            </Link>
            <div className="blog-post-meta">
              <span className="blog-post-meta-item">
                <FiUser /> {post.author || 'Admin'}
              </span>
              <span className="blog-post-meta-item">
                <FiCalendar /> {formatDate(post.published_at || post.created_at)}
              </span>
              <span className="blog-post-meta-item">
                <FiClock /> {formatReadingTime(post.content)}
              </span>
            </div>
            <h1 className="blog-post-title">{post.title}</h1>
            {post.excerpt && <p className="blog-post-excerpt">{post.excerpt}</p>}
          </motion.div>
        </div>
      </section>

      {/* Post Content */}
      <section className="blog-post-content-section">
        <div className="container">
          <motion.article
            className="blog-post-content"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {post.image && (
              <div className="blog-post-image">
                <img 
                  src={post.image.startsWith('http') ? post.image : `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${post.image}`} 
                  alt={post.title} 
                />
              </div>
            )}
            {post.content ? (
              <div
                className="blog-post-body"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            ) : (
              <div className="blog-post-body">
                <p>No content available for this post.</p>
              </div>
            )}
          </motion.article>
        </div>
      </section>

      {/* CTA Section */}
      <section className="blog-post-cta">
        <div className="container">
          <motion.div
            className="blog-post-cta-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="blog-post-cta-title">Ready to <span>Contact</span> Us?</h2>
            <p className="blog-post-cta-description">
              Have questions or need assistance? Get in touch with our team today.
            </p>
            <Link to="/contact" className="btn btn-primary btn-large">
              Contact Us Now
              <FiArrowRight />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;

